/**
 * adapter.js - Isaac No Youtube, as the trial loop sees it.
 *
 * Same interface as every other game's. See
 * `client/src/components/game/shared/trialLoop.js` for what each question is
 * for, and the two older games' adapters for the same answers about different
 * mechanics.
 *
 * ONE adapter is used by the React component AND by the test harness, so the
 * session a test plays is the session a child plays.
 *
 * This game keeps the shared planner's own vocabulary - a round has `targets`,
 * a target has an `objectId` - rather than renaming it. Door Knock renames
 * `objectId` to `creatureId` and pays for it with a translation layer in every
 * file; here the "object" is a person, and calling it an object in one place is
 * cheaper than calling it a person in five.
 */

import { GAME } from './content/index.js';
import { getLevelContent, pacingFor } from './content/levels.js';
import { canonicalPhrase } from './content/nearby.js';
import { judgeAttempt } from './logic/judgeContent.js';
import { buildPrompt, buildHint } from './logic/prompts.js';
import { createRng, planLevel, recentFrom } from '../shared/rounds.js';
import { createLineBag } from '../shared/lineBag.js';
import { requiredCapsFor } from '../../../engine/contract.js';

/**
 * @param {object} [options]
 * @param {number} [options.seed]
 * @param {() => number} [options.rng] share one stream with the rest of a session
 * @returns {object} the game, in the shape the trial loop consumes
 */
export function createAdapter({ seed = 1, rng } = {}) {
  const random = rng || createRng(seed);
  const bags = {};

  /**
   * One anti-repeat bag per (phase, rung) pool, so the same rung does not say
   * the same sentence twice running. Section 8.5 is about what the CHILD hears,
   * and at Phase 1 the child hears one rung's lines for the whole game - which
   * makes the bag matter more here than anywhere else on the platform.
   */
  const choose = (key) => (templates) => {
    if (!bags[key] || bags[key].size !== templates.length) {
      bags[key] = createLineBag(templates, { rng: random });
    }
    return bags[key].next();
  };

  // A level is replayed until its boxes have ten plays each, and a word memory
  // that resets at every pass boundary is a memory with a seam in it: the child
  // hears the same word twice across it.
  let memory = { key: null, previous: null, recent: [] };

  return {
    id: GAME.id,
    capabilities: GAME.capabilities,
    coverage: {
      phasesAvailable: GAME.phasesAvailable,
      levelsAvailable: GAME.levelsAvailable,
    },

    has: (phase, level) => Boolean(getLevelContent(phase, level)),
    content: (phase, level) => getLevelContent(phase, level),
    nominalRung: (phase, level) => getLevelContent(phase, level).nominalRung,
    pacing: (phase, level) => pacingFor(getLevelContent(phase, level)),
    requiredCaps: (phase, level) => requiredCapsFor(phase, level, GAME.capabilities),

    /** One pass through a level: rounds, each with its field and its targets. */
    plan(phase, level) {
      const content = getLevelContent(phase, level);
      const key = `${phase}.${level}`;
      const same = memory.key === key;
      const rounds = planLevel(content, random, {
        previous: same ? memory.previous : null,
        recent: same ? memory.recent : [],
      });
      memory = {
        key,
        previous: rounds[rounds.length - 1] || null,
        recent: recentFrom(rounds),
      };
      return rounds;
    },

    phraseFor: (target) => canonicalPhrase(target),
    onScreen: (target) => target.onScreen || [target.objectId],
    gridState: () => null,
    targetPosition: (target) => `card-${target.slot ?? 0}`,
    // Word frames and object choices are a Phase 3 idea (section 8.2). Nothing
    // is offered on screen here, at any rung.
    optionsVisible: () => false,

    prompt: ({ phase, rungId, target, content }) => buildPrompt({
      phase, rungId, target, content, choose: choose(`p${phase}-${rungId}`),
    }),

    hint: ({ phase, target }) => buildHint({
      phase, target, choose: choose(`hint-${phase}`),
    }),

    judge: ({ phase, target, trial, transcript, alternatives }) => judgeAttempt({
      phase, target, trial, transcript, alternatives,
    }),
  };
}

export default { createAdapter };
